import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "./ui/accordion";
import { EnquiryDialog } from "./LeadForm";
import { navItems, brand, shortDisclaimer } from "../mock/data";
import { waLink } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import RouteSeo from "./RouteSeo";
import {
  Menu, ChevronDown, Phone, Mail, MapPin, Wallet, ShieldAlert,
  Facebook, Instagram, Twitter, Youtube, Linkedin, ArrowRight, MessageCircle,
} from "lucide-react";

function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2.5 group">
      <img src="/logo-full.png" alt="Loansupportindia — lets make your dreams together"
        className="h-9 w-auto md:h-11" />
    </Link>
  );
}

function DesktopNav() {
  const { pathname } = useLocation();
  return (
    <nav className="hidden lg:flex items-center gap-1">
      {navItems.map((item) =>
        item.children ? (
          <div key={item.label} className="relative group">
            <button className="flex items-center gap-1 px-3.5 py-2 text-sm font-medium text-foreground/80 hover:text-primary transition-colors">
              {item.label}
              <ChevronDown className="h-3.5 w-3.5 transition-transform group-hover:rotate-180" />
            </button>
            <div className="absolute left-0 top-full pt-2 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200 z-50">
              <div className="min-w-[240px] rounded-xl border bg-popover p-2 shadow-xl">
                {item.children.map((c) => (
                  <Link
                    key={c.to}
                    to={c.to}
                    className="flex items-center justify-between rounded-lg px-3 py-2.5 text-sm text-foreground/80 hover:bg-secondary hover:text-primary transition-colors"
                  >
                    {c.label}
                    <ArrowRight className="h-3.5 w-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 transition-all" />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `nav-link px-3.5 py-2 text-sm font-medium transition-colors ${
                isActive || pathname === item.to ? "text-primary active" : "text-foreground/80 hover:text-primary"
              }`
            }
          >
            {item.label}
          </NavLink>
        )
      )}
    </nav>
  );
}

function MobileNav() {
  const [open, setOpen] = useState(false);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="icon" className="lg:hidden">
          <Menu className="h-5 w-5" />
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[300px] overflow-y-auto">
        <div className="mb-6 mt-2"><Logo /></div>
        <Accordion type="multiple" className="w-full">
          {navItems.map((item) =>
            item.children ? (
              <AccordionItem key={item.label} value={item.label}>
                <AccordionTrigger className="text-sm font-medium">{item.label}</AccordionTrigger>
                <AccordionContent>
                  <div className="flex flex-col gap-1 pl-2">
                    {item.children.map((c) => (
                      <Link key={c.to} to={c.to} onClick={() => setOpen(false)}
                        className="py-2 text-sm text-muted-foreground hover:text-primary">
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ) : (
              <Link key={item.to} to={item.to} onClick={() => setOpen(false)}
                className="block border-b py-3.5 text-sm font-medium hover:text-primary">
                {item.label}
              </Link>
            )
          )}
        </Accordion>
        <div className="mt-6">
          <EnquiryDialog trigger={<Button className="w-full">Check Eligibility</Button>} />
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Header() {
  return (
    <header className="sticky top-0 z-40 w-full">
      <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="container-page flex h-[68px] items-center justify-between gap-4">
          <Logo />
          <DesktopNav />
          <div className="flex items-center gap-2">
            <div className="hidden lg:block">
              <EnquiryDialog trigger={<Button className="px-5">Check Eligibility</Button>} />
            </div>
            <MobileNav />
          </div>
        </div>
      </div>
    </header>
  );
}

function Footer() {
  const { settings } = useSettings();
  const cols = [
    { title: "Company", links: [["About Us", "/about"], ["Contact Us", "/contact"], ["Careers", "/career"], ["Company Information", "/company-information"]] },
    { title: "Loans", links: [["Personal Loan", "/loans/personal-loan"], ["Business Loan", "/loans/business-loan"], ["Home Loan", "/loans/home-loan"], ["Loan Against Property", "/loans/loan-against-property"], ["Vehicle Loan", "/loans/vehicle-loan"], ["Balance Transfer", "/loans/balance-transfer"]] },
    { title: "Products & Tools", links: [["Insurance", "/insurance"], ["Credit Score", "/credit-score"], ["EMI Calculator", "/calculators/emi"], ["GST Calculator", "/calculators/gst"], ["FAQs", "/faqs"], ["Blog", "/blog"]] },
    { title: "Legal", links: [["Privacy Policy", "/privacy-policy"], ["Terms & Conditions", "/terms"], ["Disclaimer", "/disclaimer"], ["Cookie Policy", "/cookie-policy"], ["Grievance Redressal", "/grievance-redressal"]] },
    { title: "Regulatory", links: [["Lending Partners", "/lending-partners"], ["Insurance Partners", "/insurance-partners"], ["Investors", "/investors"], ["Company Information", "/company-information"]] },
  ];
  const socials = [Facebook, Instagram, Twitter, Youtube, Linkedin];
  return (
    <footer className="mt-auto bg-[#0b2b29] text-white/80">
      <div className="container-page py-14">
        <div className="grid gap-10 lg:grid-cols-6">
          <div className="lg:col-span-1">
            <div className="mb-4 inline-flex rounded-lg bg-white px-3 py-2.5">
              <img src="/logo-full.png" alt="Loansupportindia" className="h-8 w-auto" />
            </div>
            <p className="text-sm leading-relaxed text-white/60">
              A loan &amp; financial-services facilitation platform helping you find and apply for suitable options from
              authorised partners.
            </p>
            <div className="mt-5 flex gap-2">
              {socials.map((S, i) => (
                <a key={i} href="#" onClick={(e) => e.preventDefault()}
                  className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 hover:bg-primary transition-colors">
                  <S className="h-4 w-4 text-white" />
                </a>
              ))}
            </div>
          </div>
          {cols.map((col) => (
            <div key={col.title}>
              <h4 className="font-heading text-sm font-semibold text-white mb-4">{col.title}</h4>
              <ul className="space-y-2.5">
                {col.links.map(([label, to]) => (
                  <li key={label}>
                    <Link to={to} className="text-sm text-white/60 hover:text-white transition-colors">{label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 rounded-xl border border-white/10 bg-white/5 p-5">
          <p className="text-xs leading-relaxed text-white/60">
            <strong className="text-white/80">Disclaimer:</strong> {shortDisclaimer} We may receive referral/commission
            fees from partners where applicable. Insurance products are subject to policy terms and are offered by
            authorised insurers/intermediaries under the applicable IRDAI framework. We do not claim to be RBI
            Registered/Approved/Licensed unless it applies to our company.
          </p>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="container-page flex flex-col md:flex-row items-center justify-between gap-3 py-5 text-xs text-white/50">
          <span>Copyright © 2021 – {new Date().getFullYear()} by {settings.copyright_entity || settings.company_name || "[Registered Company Name]"}. All rights reserved.</span>
          <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{settings.address || "[Registered Office Address]"}</span>
            <Link to="/admin" className="hover:text-white/80">Admin</Link>
          </span>
        </div>
      </div>
    </footer>
  );
}

function CookieBanner() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!localStorage.getItem("lsi_cookie_consent")) setShow(true);
  }, []);
  if (!show) return null;
  const accept = () => { localStorage.setItem("lsi_cookie_consent", "accepted"); setShow(false); };
  const decline = () => { localStorage.setItem("lsi_cookie_consent", "declined"); setShow(false); };
  return (
    <div className="fixed bottom-4 left-1/2 z-50 w-[94%] max-w-3xl -translate-x-1/2 rounded-2xl border bg-popover p-4 shadow-2xl md:p-5">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <p className="text-sm text-muted-foreground">
          We use cookies for essential functionality and, where enabled, analytics/marketing. See our{" "}
          <Link to="/cookie-policy" className="text-primary underline">Cookie Policy</Link>.
        </p>
        <div className="flex shrink-0 gap-2">
          <Button variant="outline" size="sm" onClick={decline}>Decline</Button>
          <Button size="sm" onClick={accept}>Accept</Button>
        </div>
      </div>
    </div>
  );
}

function FloatingWhatsApp() {
  return (
    <a
      href={waLink("Hi Loansupportindia, I would like to enquire about a loan/financial product.")}
      target="_blank" rel="noopener noreferrer"
      className="fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-3 text-sm font-semibold text-white shadow-xl transition-transform hover:scale-105"
      aria-label="Enquire on WhatsApp"
    >
      <MessageCircle className="h-5 w-5" />
      <span className="hidden sm:inline">Enquire on WhatsApp</span>
    </a>
  );
}

function GreenInfoBar() {
  const { settings } = useSettings();
  return (
    <div className="bg-[#0e7a3d] text-white/90 text-xs md:text-sm">
      <div className="container-page flex flex-col items-center justify-between gap-2 py-3 md:flex-row">
        <span className="flex items-center gap-2 text-center md:text-left">
          <ShieldAlert className="h-4 w-4 shrink-0" />
          Facilitation platform — we do not directly sanction or disburse loans.
        </span>
        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1">
          {settings.phone && <span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{settings.phone}</span>}
          {settings.email && <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />{settings.email}</span>}
          <Link to="/grievance-redressal" className="font-medium hover:underline">Grievance Redressal</Link>
        </div>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { pathname } = useLocation();
  useSettings(); // subscribe so WhatsApp/contact info updates when settings load
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return (
    <div className="flex min-h-screen flex-col">
      <RouteSeo />
      <Header />
      <main className="flex-1">{children}</main>
      <GreenInfoBar />
      <Footer />
      <FloatingWhatsApp />
      <CookieBanner />
    </div>
  );
}
